import { useEffect, useState, useSyncExternalStore } from 'react'

import type { MusicCard } from '../../api/types'
import { useSettings } from '../../hooks/queries'
import {
  MUSICKIT_AKTIV,
  pause,
  playCard,
  preparedInstance,
  prepare,
  queueFor,
  rememberedSignIn,
  resume,
  signOut,
  signedIn,
  snapshot,
  subscribe,
  traceMusic,
  type MusicKitInstance,
} from '../../lib/musickit'
import { useTexte } from '../../texte'

/**
 * Musik im Chat (#102, 1.57.0): der Player des Anbieters und ein Weg nach
 * draußen.
 *
 * **Since 1.73.0 the provider's player stands there again, with the whole
 * list** -- as before 1.70.0, and decided in use: "wieder die Originalkarten
 * von Spotify und Apple anzeigen". Signing in inside Apple's player holds
 * (since its frame gets the page name, 1.72.2); whole tracks come only after
 * the frame is loaded once more, hence the button *Player neu laden* under
 * it. The own card with MusicKit (1.70.0 to 1.72.2) is switched off with
 * `MUSICKIT_AKTIV`, not removed. Die Abwägung steht in `CLAUDE.md`, in den
 * Hinweisen für den Leser und auf der Datenschutzseite.
 *
 * Zwei Dinge halten es klein, und wer sie wegnimmt, macht es groß:
 * `loading="lazy"` (ein Player weit oben im Verlauf lädt nicht, solange
 * niemand dort ist) und keine Adresse, die von selbst abspielt.
 *
 * **Beide Adressen kommen vom Server.** Hier wird nichts zusammengebaut:
 * `Music` baut sie aus geprüften Teilen, und eine zweite Stelle mit
 * derselben Regel liefe auseinander.
 *
 * **Die `sandbox`-Liste gehört zu dem, was es klein hält, und jedes Token
 * darin steht mit Grund** (#106, 1.67.0). Sie wird nicht abgeschrieben und
 * nicht ergänzt, weil es gerade passt: `tools/pruefstand/musik.mjs` hält den
 * Satz namentlich fest, in beide Richtungen.
 *
 * `allow-popups-to-escape-sandbox` kam als letztes dazu, und der Grund ist
 * eine Meldung: im Apple-Music-Player stand „Anmelden", das Fenster ging auf,
 * die Anmeldung ging — und im Player war sie danach nicht aktiv. **Ein
 * Fenster, das ein beschränkter Rahmen öffnet, erbt seine Beschränkungen**,
 * solange dieses Token fehlt. Das Anmeldefenster war damit keines, und was es
 * dem Rahmen zurückmelden wollte, kam aus einem beschränkten Kontext.
 *
 * **Es weitet nichts.** Das Fenster geht dorthin, wohin der Mitlesende selbst
 * getippt hat — denselben Weg hat der Knopf darunter ohnehin offen, ohne jede
 * Sandbox. Ob die Anmeldung danach hält, entscheidet Safari allerdings selbst:
 * Apples Rahmen muss die Storage Access API rufen (dafür steht
 * `allow-storage-access-by-user-activation` da), und Safari darf fragen oder
 * ablehnen. Der Prüfstand kann das nicht beantworten, dieses Chromium hat
 * Safaris Regeln für fremde Cookies nicht.
 *
 * **Offen, und ausdrücklich nicht in derselben Runde angefasst:**
 * `allow-top-navigation-by-user-activation` lässt den fremden Player auf Tipp
 * das ganze Fenster woandershin führen — ein Befund der Codereview. Zwei
 * Änderungen an dieser Zeile in einer Runde machen einen Fehlschlag am Gerät
 * unzuordenbar; deshalb steht es noch da.
 */
export function Musikkarte({ karte }: { karte: MusicCard }) {
  const t = useTexte().chat
  const anbieter = t.musikAnbieter[karte.provider] ?? ''
  const art = t.musikArten[karte.kind] ?? ''

  /*
   * **The own card; the frame waits for a tap** (#106, 1.70.0). Since the
   * server fetches title, artist and cover once per card, the card can stand
   * on its own. The condition is the title and not a flag of its own: without
   * it (Spotify, no key, Apple silent) everything stays as before.
   */
  const eigen = MUSICKIT_AKTIV && (karte.title ?? '') !== ''

  /*
   * **Apple Music with a key: the tap leads to MusicKit, not into Apple's
   * frame** (1.71.0). From the field: "Karte angetippt, im Player
   * angemeldet" -- and that is exactly the way that cannot hold (Apple's
   * frame, Safari, Storage Access; see `CLAUDE.md`). 1.70.0 put the way that
   * can hold as a small button *next to* the card, while the card itself
   * still led into the frame with Apple's own "Sign in". The app sent people
   * down the wrong way.
   *
   * Without `APPLE_MUSIC_KEY_ID` `/api/music/token` answers 404, and a way
   * over it would be a silent refusal; so the whole branch hangs on the
   * service being reported.
   */
  const dienste = useSettings().data?.services
  const mitMusicKit = eigen && dienste?.apple_music === true && karte.provider === 'apple_music'

  /*
   * `ruhe`: nothing loaded. `wahl`: sign in or hear a clip. `laedt`: MusicKit
   * is coming. `anmelden`: loaded, not signed in -- the next tap opens Apple's
   * window. `rahmen`: Apple's or Spotify's player.
   */
  const [phase, setPhase] = useState<'ruhe' | 'wahl' | 'laedt' | 'anmelden' | 'rahmen'>('ruhe')
  /*
   * **Reloading Apple's player** (1.73.0). From the device: signed in inside
   * the player, it still played clips; after leaving the page and coming back
   * it played whole tracks -- Safari hands the frame Apple's sign-in only on
   * its next load. Our page cannot see whether someone signed in (the frame
   * is a foreign page), so it is a button and never automatic: an automatic
   * reload would stop a track that is playing whenever the app comes back to
   * the front. A new `key` makes React build a new frame.
   */
  const [ladung, setLadung] = useState(0)
  const [fehler, setFehler] = useState<string | null>(null)
  const musik = useSyncExternalStore(subscribe, snapshot)
  const schluessel = karte.open_url
  const aktiv = musik.card === schluessel
  const spielt = aktiv && musik.playing

  async function spielen(mk: MusicKitInstance): Promise<void> {
    const was = queueFor(karte.kind, karte.ref ?? '', karte.track_ref ?? '')
    if (was === null) {
      setFehler(t.musikOhneWarteschlange)
      setPhase('ruhe')
      return
    }
    setPhase('laedt')
    try {
      await playCard(mk, schluessel, was)
      setFehler(null)
    } catch {
      // **One sentence per place**, so that the device already tells which
      // suspicion holds. The reason itself is in the trace.
      setFehler(t.musikAbspielenFehlt)
    } finally {
      setPhase('ruhe')
    }
  }

  /** Load, then play at once if a sign-in is there, else wait for the next tap. */
  async function laden(): Promise<void> {
    setFehler(null)
    setPhase('laedt')
    let mk: MusicKitInstance
    try {
      mk = await prepare()
    } catch {
      setFehler(t.musikLadenFehlt)
      setPhase('ruhe')
      return
    }
    if (mk.isAuthorized) {
      signedIn()
      await spielen(mk)
    } else {
      setPhase('anmelden')
    }
  }

  /**
   * Opens Apple's window -- **`authorize()` is the first line of the tap.**
   *
   * Safari lets a window open only out of a gesture, and the rule stands in
   * `CLAUDE.md` for the passkey already: who first waits for the server and
   * then calls the browser is outside the gesture. In 1.70.0 two `await`s
   * stood before it. The instance is here already (`laden()` ran on the tap
   * before), so nothing needs to wait.
   */
  function anmelden(): void {
    const mk = preparedInstance()
    if (mk === null) {
      void laden()
      return
    }
    const vorgang = mk.authorize()
    setFehler(null)
    setPhase('laedt')
    vorgang
      .then(() => {
        traceMusic('angemeldet')
        signedIn()
        return spielen(mk)
      })
      .catch((error: unknown) => {
        traceMusic('authorize abgelehnt', error)
        // **Look before giving up.** The window may have reached MusicKit
        // even though the promise says no -- then play.
        if (mk.isAuthorized) {
          traceMusic('trotzdem angemeldet')
          signedIn()
          void spielen(mk)
          return
        }
        setFehler(t.musikAnmeldungFehlt)
        setPhase('anmelden')
      })
  }

  /*
   * **And when the app comes back to the front, look again.** From the
   * field: in the app on the home screen Apple's window opens in another
   * context; back in the app stood the error. If the sign-in arrived after
   * all, the card goes on instead of waiting for another tap.
   */
  useEffect(() => {
    if (phase !== 'anmelden' || fehler === null) return
    function zurueck(): void {
      if (document.visibilityState !== 'visible') return
      const mk = preparedInstance()
      traceMusic(`zurück in der App, angemeldet: ${mk?.isAuthorized === true ? 'ja' : 'nein'}`)
      if (mk?.isAuthorized === true) {
        signedIn()
        void spielen(mk)
      }
    }
    document.addEventListener('visibilitychange', zurueck)
    return () => document.removeEventListener('visibilitychange', zurueck)
  })

  function tippen(): void {
    const mk = preparedInstance()
    if (aktiv && mk !== null) {
      void (spielt ? pause(mk) : resume(mk).catch(() => setFehler(t.musikAbspielenFehlt)))
      return
    }
    if (!mitMusicKit) {
      setPhase('rahmen')
      return
    }
    if (mk?.isAuthorized === true) {
      void spielen(mk)
      return
    }
    // Signed in before: go straight on, MusicKit restores it. Otherwise the
    // choice first -- nothing of Apple's loads for a clip.
    if (rememberedSignIn()) void laden()
    else setPhase('wahl')
  }

  async function abmelden(): Promise<void> {
    const mk = preparedInstance()
    if (mk === null) return
    try {
      await signOut(mk)
    } catch (error) {
      traceMusic('abmelden', error)
    }
    setPhase('ruhe')
  }

  const weg = (
    <a
      className="button button--small button--outline musikkarte__weg"
      href={karte.open_url}
      target="_blank"
      rel="noopener noreferrer"
      data-musik-weg=""
    >
      {t.musikOeffnen(anbieter)}
    </a>
  )

  if (eigen && phase !== 'rahmen') {
    return (
      <div className="musikkarte" data-musik={karte.provider} data-musik-art={karte.kind}>
        <button
          type="button"
          className="musikkarte__eigen"
          onClick={tippen}
          data-musik-start=""
          data-musik-spielt={spielt ? '' : undefined}
        >
          {(karte.cover_url ?? '') !== '' ? (
            <img
              className="musikkarte__cover"
              src={karte.cover_url}
              alt=""
              loading="lazy"
              // The page name goes to Apple's script only (1.72.2), not to
              // the image server -- see `nameOurPage()` in lib/musickit.ts.
              referrerPolicy="no-referrer"
              width={72}
              height={72}
            />
          ) : (
            <span className="musikkarte__cover musikkarte__cover--leer" aria-hidden="true" />
          )}
          <span className="musikkarte__worte">
            <span className="musikkarte__titel">{karte.title}</span>
            {(karte.artist ?? '') !== '' && (
              <span className="musikkarte__kuenstler">{karte.artist}</span>
            )}
            <span className="musikkarte__dienst">{t.musikPlayer(anbieter, art)}</span>
          </span>
          <span className="musikkarte__start" aria-hidden="true">{spielt ? '⏸' : '▶'}</span>
          <span className="visually-hidden">{spielt ? t.musikAnhalten : t.musikAbspielenBei(anbieter)}</span>
        </button>
        {/* **Without a subscription is the default** (1.71.1), and it
            stands first and filled: from the field, "als Default-Auswahl
            darstellen" -- most readers have no Apple Music subscription, and
            for them the sign-in is a dead end. */}
        {phase === 'wahl' && (
          <div className="musikkarte__wahl">
            <button
              type="button"
              className="button button--small"
              onClick={() => setPhase('rahmen')}
              data-musik-ausschnitt=""
            >
              {t.musikAusschnitt}
            </button>
            <button
              type="button"
              className="button button--small button--outline"
              onClick={() => { void laden() }}
              data-musik-anmelden=""
            >
              {t.musikAnmelden}
            </button>
          </div>
        )}
        {phase === 'laedt' && (
          <p className="leise klein" data-musik-laedt="">{t.musikLaedt}</p>
        )}
        {phase === 'anmelden' && (
          <div className="musikkarte__wahl">
            <button
              type="button"
              className="button button--small"
              onClick={() => setPhase('rahmen')}
              data-musik-ausschnitt=""
            >
              {t.musikAusschnitt}
            </button>
            <button
              type="button"
              className="button button--small button--outline"
              onClick={anmelden}
              data-musik-weiter=""
            >
              {t.musikWeiterZuApple}
            </button>
          </div>
        )}
        {fehler !== null && (
          <p className="warnend klein" role="alert" data-musik-fehler="">{fehler}</p>
        )}
        {weg}
        {mitMusicKit && musik.signedIn && aktiv && (
          <button
            type="button"
            className="button button--small button--outline"
            onClick={() => { void abmelden() }}
            data-musik-abmelden=""
          >
            {t.musikAbmelden}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="musikkarte" data-musik={karte.provider} data-musik-art={karte.kind}>
      <iframe
        key={ladung}
        className="musikkarte__player"
        src={karte.embed_url}
        title={t.musikPlayer(anbieter, art)}
        height={karte.height}
        loading="lazy"
        // Apple's frame gets the page name, Spotify's does not (1.72.2).
        // From the device: signed in inside Apple's frame after MusicKit had
        // named the page, the frame played whole tracks on the next visit --
        // before 1.72.1, when it never got a referrer, the sign-in never held.
        // The recipient is Apple either way, the same as for MusicKit; only
        // the origin goes, never the trip.
        referrerPolicy={karte.provider === 'apple_music' ? 'strict-origin-when-cross-origin' : 'no-referrer'}
        allow="encrypted-media; clipboard-write; fullscreen; picture-in-picture"
        sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
      />
      {karte.provider === 'apple_music' ? (
        <div className="musikkarte__knoepfe">
          {weg}
          <button
            type="button"
            className="button button--small button--outline"
            onClick={() => setLadung((n) => n + 1)}
            data-musik-neuladen=""
          >
            {t.musikNeuLaden}
          </button>
        </div>
      ) : weg}
    </div>
  )
}
